import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

const PAIRS = [
    {
        cube: "The King Cube",
        spirit: "Aged Bourbon",
        glass: "Rocks · 10 oz",
        detail:
            "A monolithic block dilutes with the patience of a cathedral. The bourbon opens slowly — vanilla, leather, smoked oak — across twenty unhurried minutes.",
        temp: "−8°C",
    },
    {
        cube: "The Diamond",
        spirit: "Negroni",
        glass: "Tumbler · 8 oz",
        detail:
            "Thirty-six faceted planes keep the bitters sharp and the Campari bright. No rounding of edges. An honest drink, held accountable by honest ice.",
        temp: "−6°C",
    },
    {
        cube: "The Diamond",
        spirit: "Brut Champagne",
        glass: "Coupe · 6 oz",
        detail:
            "Hand-faceted planes refract golden bubbles. More jewellery than ice. Reserved for anniversaries, reconciliations, and quiet wins.",
        temp: "−4°C",
    },
    {
        cube: "The Shard",
        spirit: "Japanese Whisky Highball",
        glass: "Collins · 14 oz",
        detail:
            "A single vertical shard from rim to base. The effervescence climbs its spine; the whisky stays whisper-cold, never watered.",
        temp: "−10°C",
    },
];

export default function Pairings() {
    const [active, setActive] = useState(0);
    const pair = PAIRS[active];

    return (
        <section
            id="pairings"
            data-testid="pairings-section"
            className="relative bg-obsidian-900 py-24 md:py-32 lg:py-40"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, ease }}
                        className="col-span-12 md:col-span-8"
                    >
                        <span className="overline">§ 02 — Pairings</span>
                        <h2 className="mt-6 font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                            Every cube <br />
                            has a <span className="italic text-amber-glow">conversation</span>.
                        </h2>
                    </motion.div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 border-t border-white/10 pt-10">
                    {/* List */}
                    <div className="lg:col-span-5">
                        <ul className="space-y-1" data-testid="pairings-list">
                            {PAIRS.map((p, i) => (
                                <li key={p.spirit}>
                                    <button
                                        data-testid={`pairing-tab-${i}`}
                                        onClick={() => setActive(i)}
                                        className={`group w-full text-left flex items-baseline justify-between py-6 border-b border-white/10 transition-colors ${
                                            active === i ? "text-ice-50" : "text-ice-400 hover:text-ice-200"
                                        }`}
                                    >
                                        <span className="flex items-baseline gap-5">
                                            <span
                                                className={`font-script text-xl transition-colors ${
                                                    active === i ? "text-amber-glow" : "text-ice-400/50"
                                                }`}
                                            >
                                                0{i + 1}
                                            </span>
                                            <span>
                                                <span className="font-serif-display text-3xl md:text-4xl block leading-none">
                                                    {p.spirit}
                                                </span>
                                                <span className="text-[0.68rem] uppercase tracking-[0.3em] text-ice-400 mt-2 inline-block">
                                                    {p.cube}
                                                </span>
                                            </span>
                                        </span>
                                        <span
                                            className={`transition-all duration-500 text-amber-glow ${
                                                active === i ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"
                                            }`}
                                        >
                                            →
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Detail panel */}
                    <div className="lg:col-span-7 relative min-h-[420px]">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.6, ease }}
                                className="relative border border-white/10 bg-obsidian-800/60 backdrop-blur-md p-8 md:p-12"
                                data-testid="pairing-detail"
                            >
                                <div className="flex items-start justify-between mb-8">
                                    <div>
                                        <span className="overline block">Currently</span>
                                        <h3 className="font-serif-display italic text-3xl md:text-4xl text-amber-glow mt-2">
                                            {pair.cube}
                                        </h3>
                                    </div>
                                    <div className="text-right">
                                        <span className="font-serif-display text-4xl md:text-5xl block">
                                            {pair.temp}
                                        </span>
                                        <span className="text-[0.65rem] uppercase tracking-[0.3em] text-ice-400">
                                            Serving Point
                                        </span>
                                    </div>
                                </div>

                                <div className="hairline mb-8" />

                                <p className="text-ice-200/80 text-lg leading-relaxed max-w-xl">
                                    {pair.detail}
                                </p>

                                <div className="mt-10 flex items-center gap-6 text-[0.72rem] uppercase tracking-[0.28em] text-ice-400">
                                    <span>{pair.glass}</span>
                                    <span className="w-px h-4 bg-white/20" />
                                    <span>Pour 2 oz · Stir twelve times</span>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}
